import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../basic/ThemeProvider';
import { Button } from '../../basic/Button';
import CyclicWaveBackground from '../../basic/CyclicWaveBackground';
import { endConfig, startConfig } from '../Landing/HeroSection/waveConfigs';
import ThemeSwitcher from '../../basic/ThemeSwitcher';
import { landingPageContent } from '../../../content/landing';
import ProductsSection from './ProductsSection';
import { ModeTab } from '../../composite/ModeTabs';
import styles from './LandingWithSections.module.css';
import SocialHandles from './SocialHandles';
import { loadStoredTokens } from '../Auth/services/tokenStore';

const productTabs: ModeTab[] = [
  {
    id: 'cognitive-media',
    label: 'CineMatrix',
    title: 'CineMatrix',
    description: 'AI-powered characters, generative story arcs, and living narratives. Media that thinks, adapts, and evolves.',
    link: {
      url: '/game/blog/cognitive-media',
      text: 'Read More',
    },
    media: {
      type: 'youtube' as const,
      src: 'https://www.youtube.com/embed/ajjJ_mO1X1Y?start=5',
    },
  },
  {
    id: 'beam',
    label: 'News',
    title: 'Autonomous News',
    description: 'Realtime events fetched from AI search engines like xAI, Grok and Perplexity. Broadcast live on social media.',
    link: {
      url: '/game/newsng',
      text: 'Watch Live News',
    },
    media: {
      type: 'video' as const,
      src: '/game/videos/beam/intro.mp4',
    },
  },
  {
    id: 'play',
    label: 'GameShows',
    title: 'Game Shows',
    description: 'Build Social Interactive games with AI as the game master. From Trivia to Dungeons and Dragons, creativity powered by vMonad MediaGen.',
    link: {
      url: '/game/gameshow',
      text: 'Check out the full game here',
    },
    media: {
      type: 'video' as const,
      src: '/game/videos/play/intro.mp4',
    },
  },
  {
    id: 'deepvision',
    label: 'MediaIQ',
    title: 'MediaIQ',
    description: 'Visual understanding, reasoning, and perception. AI-powered analysis of images and video for scene understanding, object detection, and contextual intelligence.',
    link: {
      url: '/game/vision',
      text: 'Try DeepVision',
    },
    media: {
      type: 'youtube' as const,
      src: 'https://www.youtube.com/embed/VYTcuK_TYqk',
    },
  },
  {
    id: 'cast',
    label: 'Cast',
    title: 'Cast',
    description: 'Create and manage digital actors and agents. Build persistent AI characters with unique voices, appearances, and personalities for your media productions.',
  },
];

/**
 * NextGenLanding Component
 * A modern landing page with smooth vertical scrolling sections
 * Built on top of the original Landing styles
 */
const LandingWithSections: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { companyHeader, productsSection } = landingPageContent;
  const navigate = useNavigate();
  const [isSignedIn, setIsSignedIn] = useState(false);

  useEffect(() => {
    const tokens = loadStoredTokens();
    setIsSignedIn(tokens !== null);
  }, []);

  const handleStudioClick = () => {
    if (isSignedIn) {
      navigate('/studio');
    } else {
      navigate('/auth/login?redirect=/studio');
    }
  };

  // Handle hash navigation (e.g., #products)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const targetId = hash.substring(1); // Remove the # symbol
      const element = document.getElementById(targetId);
      if (element) {
        // Small delay to ensure the page is fully loaded
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, []);

  return (
    <div className={styles['landing-sections-root']}>
      {/* Fixed-position wave background at the top */}
      <div className={styles['waves-bg-fixed']}>
        <CyclicWaveBackground
          startConfig={startConfig}
          endConfig={endConfig}
          cycleConfig={{
            cycleDuration: 20000,
            startWaitTime: 0.00,
            endWaitTime: 0.00,
            transitionTime: 0.495
          }}
        />
      </div>
      <div className={styles['theme-switcher-wrapper']}>
        <SocialHandles height={24} />
        <Button size="small" onClick={handleStudioClick}>
          {isSignedIn ? 'Studio' : 'Login'}
        </Button>
        <ThemeSwitcher isDark={isDark} onToggle={toggleTheme} />
      </div>
      {/* All scrollable content - single continuous page */}
      <div className={styles['landing-sections-content']}>
        <ProductsSection
          content={productsSection}
          productTabs={productTabs}
          companyName={companyHeader.companyName}
          isSignedIn={isSignedIn}
        />
      </div>
    </div>
  );
};

export default LandingWithSections;